import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useEditMode } from '../contexts/EditModeContext';
import { getEffectiveStaffFeatureIds } from '../config/staffFeatures';
import './AdminPanel.css';

/**
 * Estructura del panel de control.
 * Cada tarjeta incluye un ícono, un título, una descripción breve y un tono de acento.
 * El tono mapea a una variable CSS en `AdminPanel.css` (`--accent-*`).
 */
const PANEL_SECTIONS = [
  {
    id: 'ventas',
    title: 'Ventas',
    subtitle: 'Operación diaria y reportes',
    items: [
      {
        to: '/local',
        feature: 'localSales',
        label: 'Registrar ventas en local',
        description: 'Cargá ventas de mostrador y datos de facturación.',
        icon: '🛒',
        tone: 'emerald'
      },
      {
        to: '/historial-ventas',
        feature: 'salesHistory',
        label: 'Ver historial de ventas',
        description: 'Consultá tickets y operaciones vendidas.',
        icon: '🧾',
        tone: 'emerald'
      },
      {
        to: '/metricas',
        feature: 'salesMetrics',
        label: 'Ver métricas de ventas',
        description: 'Revisá KPIs e indicadores por período.',
        icon: '📊',
        tone: 'indigo'
      },
      {
        to: '/grafico',
        feature: 'salesChart',
        label: 'Ver gráfico de ventas',
        description: 'Visualizá el rendimiento estilo Wall Street.',
        icon: '📈',
        tone: 'indigo'
      }
    ]
  },
  {
    id: 'catalogo',
    title: 'Catálogo y stock',
    subtitle: 'Productos, precios y promociones',
    items: [
      {
        to: '/gestion-stock',
        feature: 'stockManagement',
        label: 'Gestionar inventario',
        description: 'Actualizá stock, costos y reposiciones.',
        icon: '📦',
        tone: 'amber'
      },
      {
        to: '/admin/proveedores',
        feature: 'suppliers',
        label: 'Gestionar proveedores',
        description: 'Consultá compras, fichas y deudas.',
        icon: '🤝',
        tone: 'amber'
      },
      {
        to: '/editor-masivo',
        feature: 'massEditor',
        label: 'Editar productos en lote',
        description: 'Modificá precios y datos de varios productos.',
        icon: '✏️',
        tone: 'amber'
      },
      {
        to: '/configuracion-descuentos',
        feature: 'discountConfig',
        label: 'Configurar descuentos',
        description: 'Administrá promociones, cupones y reglas.',
        icon: '🏷️',
        tone: 'amber'
      }
    ]
  },
  {
    id: 'clientes',
    title: 'Clientes y contactos',
    subtitle: 'Gestión de relaciones',
    items: [
      {
        to: '/admin/clientes',
        feature: 'adminCustomers',
        label: 'Gestionar clientes',
        description: 'Revisá fichas, historial y datos fiscales.',
        icon: '👥',
        tone: 'violet'
      },
      {
        to: '/subscriptores',
        feature: 'subscribers',
        label: 'Ver suscriptores',
        description: 'Consultá las altas al newsletter.',
        icon: '📧',
        tone: 'violet'
      }
    ]
  },
  {
    id: 'qr',
    title: 'QR y entradas',
    subtitle: 'Eventos, cortesías y control de acceso',
    items: [
      {
        to: '/admin/eventos',
        feature: 'adminEvents',
        label: 'Gestionar eventos',
        description: 'Creá, activá y revisá eventos de entradas.',
        icon: '🗂️',
        tone: 'rose'
      },
      {
        to: '/free',
        feature: 'freeTickets',
        label: 'Crear entradas gratis',
        description: 'Generá cortesías, invitados y tickets sin cargo.',
        icon: '🎟️',
        tone: 'rose'
      },
      {
        to: '/escanear-qr',
        feature: 'qrScanner',
        label: 'Escanear entradas QR',
        description: 'Validá entradas en puerta.',
        icon: '📷',
        tone: 'rose'
      },
      {
        to: '/qr-lista',
        feature: 'qrList',
        label: 'Ver ingresos escaneados',
        description: 'Consultá QR validados y accesos gratuitos.',
        icon: '✅',
        tone: 'rose'
      }
    ]
  },
  {
    id: 'admin',
    title: 'Administración',
    subtitle: 'Seguridad y accesos',
    items: [
      {
        to: '/admin/usuarios-staff',
        adminOnly: true,
        label: 'Gestionar usuarios',
        description: 'Creá usuarios, roles y permisos de acceso.',
        icon: '🔐',
        tone: 'slate'
      }
    ]
  }
];

const AdminPanel = () => {
  const { userRole, isAdmin, allowedFeatures } = useEditMode();
  const normalizedRole = String(userRole || '').toLowerCase();
  const effectiveFeatures = useMemo(
    () => getEffectiveStaffFeatureIds(userRole, allowedFeatures),
    [allowedFeatures, userRole]
  );
  const canAccessFeature = useMemo(
    () => (featureId) => isAdmin || effectiveFeatures.includes(featureId),
    [effectiveFeatures, isAdmin]
  );

  const roleLabel = useMemo(() => {
    if (isAdmin) return 'Administrador';
    if (normalizedRole === 'cashier') return 'Cajero';
    if (userRole === 'staff') return 'Staff';
    if (userRole === 'viewer') return 'Solo lectura';
    if (normalizedRole === 'qrscanner') return 'Scanner QR';
    return 'Usuario';
  }, [isAdmin, normalizedRole, userRole]);

  const todayLabel = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('es-AR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(new Date());
    } catch (_) {
      return new Date().toLocaleDateString();
    }
  }, []);

  const visibleSections = useMemo(
    () =>
      PANEL_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          if (item.adminOnly) return isAdmin;
          if (!item.feature) return isAdmin;
          return canAccessFeature(item.feature);
        })
      })).filter((section) => section.items.length > 0),
    [canAccessFeature, isAdmin]
  );

  const totalQuickActions = useMemo(
    () => visibleSections.reduce((sum, s) => sum + s.items.length, 0),
    [visibleSections]
  );

  return (
    <div className="adminPanel-container">
      <header className="adminPanel-header">
        <div className="adminPanel-headerMain">
          <span className="adminPanel-eyebrow">Panel de control</span>
          <h1 className="adminPanel-title">
            Centro de <span className="adminPanel-titleAccent">Operaciones</span>
          </h1>
          <p className="adminPanel-subtitle">
            Gestioná ventas, catálogo, clientes y accesos desde un único lugar.
          </p>
        </div>
        <div className="adminPanel-headerMeta" aria-hidden="false">
          <div className="adminPanel-metaItem">
            <span className="adminPanel-metaLabel">Rol</span>
            <span className="adminPanel-metaValue">{roleLabel}</span>
          </div>
          <div className="adminPanel-metaDivider" />
          <div className="adminPanel-metaItem">
            <span className="adminPanel-metaLabel">Fecha</span>
            <span className="adminPanel-metaValue adminPanel-metaValue--date">{todayLabel}</span>
          </div>
          <div className="adminPanel-metaDivider" />
          <div className="adminPanel-metaItem">
            <span className="adminPanel-metaLabel">Accesos</span>
            <span className="adminPanel-metaValue">{totalQuickActions}</span>
          </div>
        </div>
      </header>

      <main className="adminPanel-main">
        {visibleSections.map((section) => (
          <section key={section.id} className="adminPanel-section" aria-label={section.title}>
            <div className="adminPanel-sectionHead">
              <h2 className="adminPanel-sectionTitle">{section.title}</h2>
              <span className="adminPanel-sectionSubtitle">{section.subtitle}</span>
            </div>
            <div className={`adminPanel-grid adminPanel-grid--${section.id}`}>
              {section.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`adminPanel-card adminPanel-card--${item.tone}`}
                  data-tone={item.tone}
                >
                  <span className="adminPanel-cardIcon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="adminPanel-cardBody">
                    <span className="adminPanel-cardLabel">{item.label}</span>
                    <span className="adminPanel-cardDescription">{item.description}</span>
                  </span>
                  <span className="adminPanel-cardArrow" aria-hidden="true">
                    →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
};

export default AdminPanel;
